// src/components/common/Header.tsx
import React from 'react';
import {
  AppBar,
  Box,
  IconButton,
  Typography,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { TaskInfo } from '../../models/types';
import { useTheme } from '../../context/ThemeContext';
import { useHub } from '../../context/hub';
import { useAuth } from '../../context/auth';
import PersonIcon from '@mui/icons-material/Person';
import DenseHeaderMetricStrip from '../cards/DenseHeaderMetricStrip';
import { buildHeaderMetricModels } from '../../presentation/cards/models';
import type { DenseHeaderMetricModel } from '../../presentation/cards/types';

interface HeaderProps {
  taskInfo: TaskInfo | null;
  loading: boolean;
  // Remove hub related props
  showMetrics?: boolean;
  isTaskSummary?: boolean;
  isUserInfo?: boolean;
  onBackToSummary?: () => void;
  titleOverride?: string;
  showHubSelector?: boolean;
  headerAddon?: React.ReactNode;
  metricStripItems?: DenseHeaderMetricModel[];
  onHubChange?: (hubId: string) => void;
}

const Header: React.FC<HeaderProps> = ({ 
  taskInfo, 
  loading, 
  showMetrics = false,
  isTaskSummary = false,
  isUserInfo = false,
  onBackToSummary,
  titleOverride,
  showHubSelector,
  headerAddon,
  metricStripItems,
  onHubChange,
}) => {
  const { mode, toggleTheme } = useTheme();
  const { logout, isAuthenticated, userData } = useAuth();
  const { hubs, currentHubId, loading: hubsLoading, changeHub } = useHub();
  
  console.log('[Header] Rendering with hub state:', { 
    hubs, 
    currentHubId, 
    hubsLoading, 
    isTaskSummary,
    isUserInfo
  });
  
  // Handle hub selection change
  const handleHubChange = (event: SelectChangeEvent) => {
    const hubId = event.target.value;
    console.log('[Header] User selected hub:', hubId, 'Current hub:', currentHubId, 'View type:', isUserInfo ? 'UserInfo' : isTaskSummary ? 'TaskSummary' : 'TaskDetail');
    
    // Only change hub if it's different from current
    if (hubId !== currentHubId) {
      console.log('[Header] Changing hub to:', hubId);
      // Just update the hub context - the individual views will handle their own updates
      changeHub(hubId);
      onHubChange?.(hubId);
    }
  };
  
  // Handle user logout
  const handleLogout = () => {
    logout();
  };

  const isDefaultTaskDetailTitle = !titleOverride && !isTaskSummary && !isUserInfo;
  
  // Determine the title based on the view type
  const getHeaderTitle = () => {
    if (titleOverride) {
      return titleOverride;
    }
    if (isUserInfo) {
      return 'User Info';
    } else if (isTaskSummary) {
      return 'Tasks Summary';
    } else {
      return taskInfo?.taskId ?? 'Task Detail';
    }
  };

  const shouldShowHubSelector = showHubSelector ?? (isTaskSummary || isUserInfo);
  const metricRows = metricStripItems ?? buildHeaderMetricModels(taskInfo);

  return (
    <AppBar 
      position="static" 
      color="transparent" 
      elevation={0}
      sx={{
        borderBottom: `1px solid ${mode === 'dark' ? 'divider' : 'divider'}`,
        backgroundColor: mode === 'dark' ? 'background.paper' : '#fff',
        paddingX: 1.5,
        paddingY: 0.5,
        minHeight: 44,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {!isTaskSummary && !isUserInfo && (
          <IconButton 
            edge="start" 
            onClick={onBackToSummary}
            sx={{ marginRight: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <img
          src={process.env.PUBLIC_URL + '/transparent_logo.png'}
          alt="DecisionOpt"
          style={{ height: 34, marginRight: 8, opacity: 0.98 }}
        />
        <Typography 
          variant="h6" 
          component="div" 
          sx={{
            fontSize: isDefaultTaskDetailTitle ? 15 : undefined,
            fontWeight: isDefaultTaskDetailTitle ? 400 : 600,
            lineHeight: 1.05,
            letterSpacing: 0,
          }}
        >
          {getHeaderTitle()}
        </Typography>

        {headerAddon}
        
        {shouldShowHubSelector && isAuthenticated && (
          <FormControl 
            variant="outlined" 
            size="small" 
            sx={{ 
              marginLeft: 2.25,
              minWidth: 180
            }}
          >
            <Select
              value={currentHubId || ''}
              onChange={handleHubChange}
              displayEmpty
              disabled={hubsLoading || hubs.length === 0}
              startAdornment={hubsLoading ? (
                <CircularProgress size={16} sx={{ marginRight: 1 }} />
              ) : (
                <WarehouseIcon fontSize="small" sx={{ marginRight: 1 }} />
              )}
            >
              {hubs.length === 0 ? (
                <MenuItem value="" disabled>
                  No origins available
                </MenuItem>
              ) : (
                hubs.map((hub) => (
                  <MenuItem key={hub.hubId} value={hub.hubId}>
                    {hub.hubName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        {showMetrics && metricRows.length > 0 && (
          <Box
            sx={{
              mr: 1.5,
              overflow: 'hidden',
            }}
          >
            <DenseHeaderMetricStrip items={metricRows} />
          </Box>
        )}
        
        <Tooltip title="Toggle theme">
          <IconButton onClick={toggleTheme} edge="end">
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Tooltip>
        
        <Tooltip title={userData?.email || 'Profile'}>
          <IconButton edge="end" sx={{ marginLeft: 1 }}>
            <PersonIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Logout">
          <IconButton 
            edge="end" 
            onClick={handleLogout} 
            sx={{ marginLeft: 1 }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </div>
    </AppBar>
  );
};

export default Header;
